/**
 * The catalogue of MATERIALS: what a thing in the world is made of, and how it looks.
 *
 * This file is the catalogue's ONLY home: the game does not draw a material any more (the look of a
 * track is the picture this painter exports), so what the game keeps is the behaviour alone — the same
 * names and their impact kinds, in `gpo2/src/client/public/js/rendering/materials.js`. A material added
 * here is offered by the editor and painted immediately; it needs an entry over there only when the
 * `impact`/`extends` below is not the kind of impact it shares.
 *
 * A material is a NAME plus a look. The name is the contract — the track file carries it
 * (`material` on an obstacle, which the game also uses for the impact sound, and `surface` on a piece
 * of asphalt) — while the LOOK lives here and can change without touching a single track.
 *
 * Which name a thing ends up with, most specific first:
 *   1. the object itself   `obstacle.material`;
 *   2. the track           `materials.byType.<type>` (the default for a type, per track);
 *   3. this file           MATERIAL_DEFAULTS, then the material's own definition.
 * A name the catalogue doesn't know falls back to DEFAULT_MATERIAL instead of failing: a track written
 * by a newer editor still has to load in an older build, painting something reasonable.
 *
 * The look is DATA, not code: the painter reads it and builds its textures from it. A look with a tyre
 * `unit` is stamped with whole tyres — its sprites, one per tyre, so nothing is ever sliced —, a look with
 * `strip` repeats a transparent fence along a path, `composite` builds modular seating,
 * `variants` is ONE sprite fitted to a rectangle (`houses`, `signals`), and one with none is built out of
 * colours and its own grain (`wall`).
 *
 * The sprites it names (`materials/**`, `textures/materials/**`) live in this project too
 * (`public/img/…`): `npm run sync:assets` no longer copies them from the game, which no longer has them.
 */

/**
 * One complete tyre per transparent cell: black, red, then white. Flat artwork, because a tyre is drawn
 * about eleven pixels wide once the picture is baked — a tread pattern at that size is noise.
 */
export const TYRE_SPRITE_SRC = 'materials/tyres/stacks.png';
const TYRE_LOOK = {
  unit: 10,
  image: { src: TYRE_SPRITE_SRC, cellWidth: 128, cellHeight: 128, columns: 1, rows: 3 },
  base: '#26272b',
  contour: { colour: '#232428', width: 2.5 },
  impact: 'tyre',
};

export const MATERIALS = {
  /** Collision geometry only: no texture, fill, contour or shadow. */
  none: {
    invisible: true,
    impact: 'wall',
  },
  /**
   * Concrete: one continuous cast wall, no panel joints — nothing breaks the run of the grain.
   *
   * Weathered, not moulded: the greys are warm and slightly dirty (a cool even grey is what makes a
   * surface read as plastic), the aggregate in `makeBarrierTile` is coarse enough to see, `joint` is the
   * colour of its air pits and hairlines, and `crown`/`shade` are the two faces of the wall — the lit
   * top and the side away from the sun. One lit band on its own is a sheen; the pair gives thickness.
   */
  /**
   * A concrete wall, painted from its own tileable texture (`image`) — one continuous cast surface, no
   * panel joints. The colours below are the fallback the painter draws with while the texture is still
   * loading (or if the file is missing); the contour, the dirt at its foot and the shadow are drawn over
   * the texture exactly as they were over the painted one.
   */
  wall: {
    image: { src: 'textures/materials/wall1.jpg' },
    base: '#9d9b94', light: '#b6b3ab', dark: '#76746e', joint: '#54524d',
    contour: { colour: '#4d4b47', width: 2.5 },
    dirt: { colour: '#4b463c', alpha: 0.26 },
    crown: { colour: '#bdbab2', alpha: 0.36, offset: -1.6, scale: 0.4 },
    shade: { colour: '#6f6d67', alpha: 0.22, offset: 1.8, scale: 0.42 },
  },
  /**
   * A second wall texture: it `extends` the wall above, so every property — colours, contour, dirt, the
   * impact sound and the sparks — is the wall's, and the ONLY thing this material changes is the picture.
   * That is what a texture variant is: pick it in the editor and the wall is made of something else and
   * behaves exactly the same.
   */
  wall2: {
    extends: 'wall',
    image: { src: 'textures/materials/wall2.jpg' },
  },
  /** Transparent rails and posts, repeated along a wall/polyline rather than filling its body. */
  fence: {
    label: 'fence',
    types: ['wall', 'poly'],
    strip: 'fence',
    image: { src: 'textures/materials/fence.png' },
    impact: 'wall',
  },
  /** The same metal fence mounted on a low, continuous concrete footing. */
  'fence+wall': {
    label: 'fence+wall',
    types: ['wall', 'poly'],
    strip: 'fence',
    image: { src: 'textures/materials/fence.png' },
    plinth: { width: 0.82, contour: '#56564f', base: '#98978d', crown: '#b6b3a7' },
    impact: 'wall',
  },
  // Keep the old `tyre` file value: existing tracks still name the black tyres.
  tyre: {
    ...TYRE_LOOK, label: 'black tyres', tyreColours: 'black',
  },
  tyre_red: {
    ...TYRE_LOOK, label: 'red tyres', tyreColours: 'red',
  },
  tyre_white: {
    ...TYRE_LOOK, label: 'white tyres', tyreColours: 'white',
  },
  tyre_alternate: {
    ...TYRE_LOOK, label: 'alternate red/white tyres', tyreColours: 'alternate-red-white',
  },
  tyre_alternate_black_red: {
    ...TYRE_LOOK, label: 'alternate black/red tyres', tyreColours: 'alternate-black-red',
  },
  tyre_random: {
    ...TYRE_LOOK, label: 'random tyres', tyreColours: 'mixed',
  },
  /** Modular seating: fixed-size frame sections and a random crowd on a regular grid.
   * The old complete sprites remain source artwork; legacy `variant` fields are ignored. */
  stands: {
    composite: 'stand',
    rectOnly: true,
    image: { src: 'materials/stands/1.png' },
    base: '#3c4531',
  },
  /**
   * Circuit buildings and paddock props: ONE sprite fitted to the rectangle the way a stand is — the
   * same `variants` mechanism (own `length` x `depth`, variant picked by the obstacle, rectangles only),
   * which is what the editor's ratio lock and the painter's cover-fit read for both.
   *
   * The transparent sprites in `materials/houses/` use a vertical orthographic view. Variants 1-10
   * are circuit buildings; 11-22 add canopies, parasols, podium terraces, a communications tower,
   * a flag marshal cabin and two buildings with open terraces; 23-25 are a parked-car row in three
   * liveries, 26-27 two container yards and 28 a glass lens. Legacy variant indexes stay stable.
   */
  houses: {
    variants: [
      { name: '1', src: 'materials/houses/1.png', length: 670, depth: 674 },
      { name: '2', src: 'materials/houses/2.png', length: 648, depth: 633 },
      { name: '3', src: 'materials/houses/3.png', length: 408, depth: 310 },
      { name: '4', src: 'materials/houses/4.png', length: 237, depth: 240 },
      { name: '5', src: 'materials/houses/5.png', length: 299, depth: 288 },
      { name: '6', src: 'materials/houses/6.png', length: 315, depth: 292 },
      { name: '7', src: 'materials/houses/7.png', length: 408, depth: 227 },
      { name: '8', src: 'materials/houses/8.png', length: 237, depth: 227 },
      { name: '9', src: 'materials/houses/9.png', length: 299, depth: 268 },
      { name: '10', src: 'materials/houses/10.png', length: 315, depth: 271 },
      { name: '11', src: 'materials/houses/11.png', length: 256, depth: 256 },
      { name: '12', src: 'materials/houses/12.png', length: 256, depth: 256 },
      { name: '13', src: 'materials/houses/13.png', length: 384, depth: 224 },
      { name: '14', src: 'materials/houses/14.png', length: 192, depth: 192 },
      { name: '15', src: 'materials/houses/15.png', length: 192, depth: 192 },
      { name: '16', src: 'materials/houses/16.png', length: 192, depth: 192 },
      { name: '17', src: 'materials/houses/17.png', length: 384, depth: 384 },
      { name: '18', src: 'materials/houses/18.png', length: 384, depth: 384 },
      { name: '19', src: 'materials/houses/19.png', length: 320, depth: 320 },
      { name: '20', src: 'materials/houses/20.png', length: 280, depth: 196 },
      { name: '21', src: 'materials/houses/21.png', length: 480, depth: 320 },
      { name: '22', src: 'materials/houses/22.png', length: 400, depth: 360 },
      { name: '23', src: 'materials/houses/23.png', length: 361, depth: 133 },
      { name: '24', src: 'materials/houses/24.png', length: 361, depth: 133 },
      { name: '25', src: 'materials/houses/25.png', length: 361, depth: 133 },
      { name: '26', src: 'materials/houses/26.png', length: 133, depth: 177 },
      { name: '27', src: 'materials/houses/27.png', length: 187, depth: 122 },
      { name: '28', src: 'materials/houses/28.png', length: 160, depth: 157 },
    ],
  },
  /**
   * Signals: the boards along a track — sponsors, flags, the arrow that says which way the road goes.
   * Same mechanism as a stand or a house (ONE sprite fitted to the rectangle, own `length` x `depth` per
   * variant, rectangles only), because a board is a flat rectangle and that is exactly what the rectangle
   * IS: the editor locks the shape to the sprite's proportions and the sprite then fits it exactly.
   *
   * Every board is a flat drawing: a coloured field, a simple mark and a wordmark. `length` and `depth` are
   * the world size a board of that picture takes, while the PNG is rendered at three times that size, so it
   * stays sharp when the map is zoomed in.
   */
  signals: {
    variants: [
      { name: 'Arrow', src: 'materials/signals/1.png', length: 213, depth: 75 },
      { name: 'GRIPPO', src: 'materials/signals/2.png', length: 214, depth: 75 },
      { name: 'MAREA', src: 'materials/signals/3.png', length: 221, depth: 75 },
      { name: 'OCTANE', src: 'materials/signals/4.png', length: 222, depth: 75 },
      { name: 'NOVA', src: 'materials/signals/5.png', length: 228, depth: 75 },
      { name: 'VERDE', src: 'materials/signals/6.png', length: 222, depth: 75 },
      { name: 'Tommy Rulez', src: 'materials/signals/7.png', length: 296, depth: 75 },
      { name: 'CLOUDIA', src: 'materials/signals/8.png', length: 213, depth: 75 },
      { name: 'PIXELIA', src: 'materials/signals/9.png', length: 213, depth: 75 },
      { name: 'WAVEON', src: 'materials/signals/10.png', length: 221, depth: 75 },
      { name: 'ZERO 9', src: 'materials/signals/11.png', length: 224, depth: 75 },
      { name: 'GPOR', src: 'materials/signals/12.png', length: 341, depth: 75 },
      { name: 'Dante II', src: 'materials/signals/13.png', length: 211, depth: 75 },
      { name: 'Kill Keis', src: 'materials/signals/14.png', length: 203, depth: 75 },
      { name: 'BYTELINE', src: 'materials/signals/15.png', length: 213, depth: 75 },
      { name: 'COMUNE BANK', src: 'materials/signals/16.png', length: 213, depth: 74 },
      { name: 'LUPA', src: 'materials/signals/17.png', length: 239, depth: 74 },
      { name: 'ORBITA', src: 'materials/signals/18.png', length: 311, depth: 74 },
      { name: 'LUMEN', src: 'materials/signals/19.png', length: 236, depth: 74 },
      { name: 'VOLTA', src: 'materials/signals/20.png', length: 215, depth: 74 },
      { name: 'NOVATECH', src: 'materials/signals/21.png', length: 225, depth: 74 },
      { name: 'SiM Chappion', src: 'materials/signals/22.png', length: 186, depth: 74 },
      { name: 'Checkers', src: 'materials/signals/23.png', length: 187, depth: 74 },
      { name: 'SC', src: 'materials/signals/24.png', length: 166, depth: 74 },
      { name: 'Stripes', src: 'materials/signals/25.png', length: 166, depth: 74 },
      { name: 'Italy', src: 'materials/signals/26.png', length: 162, depth: 74 },
      { name: 'France', src: 'materials/signals/27.png', length: 149, depth: 74 },
      { name: 'UK', src: 'materials/signals/28.png', length: 169, depth: 74 },
      { name: 'Speed-Lap', src: 'materials/signals/29.png', length: 230, depth: 74 },
      { name: 'GP1 World', src: 'materials/signals/30.png', length: 336, depth: 74 },
      { name: 'Number 3', src: 'materials/signals/31.png', length: 142, depth: 74 },
      { name: 'Number 2', src: 'materials/signals/32.png', length: 141, depth: 74 },
      { name: 'Number 1', src: 'materials/signals/33.png', length: 141, depth: 74 },
      { name: 'tau zero', src: 'materials/signals/34.png', length: 226, depth: 75 },
      { name: 'bitgrapes', src: 'materials/signals/35.png', length: 240, depth: 75 },
      { name: 'STUMMI TAPPO', src: 'materials/signals/36.png', length: 296, depth: 75 },
      { name: 'TOPOGRAFO', src: 'materials/signals/37.png', length: 236, depth: 75 },
      { name: 'WORK IN PROGRESS', src: 'materials/signals/38.png', length: 230, depth: 75 },
      { name: 'MEN AT WORK', src: 'materials/signals/39.png', length: 229, depth: 75 },
      { name: 'RAIN MAN', src: 'materials/signals/40.png', length: 250, depth: 75 },
      { name: 'MIKHAIL TAL', src: 'materials/signals/41.png', length: 252, depth: 75 },
      { name: 'GIORGIONE', src: 'materials/signals/42.png', length: 265, depth: 75 },
      { name: 'DDDMDE', src: 'materials/signals/43.png', length: 264, depth: 75 },
      { name: 'OHM', src: 'materials/signals/44.png', length: 257, depth: 75 },
    ],
  },
};

/** The names the editor offers and a track file may carry. */
export const MATERIAL_NAMES = Object.keys(MATERIALS);

/** What a thing is made of when nothing says otherwise. */
export const DEFAULT_MATERIAL = 'wall';

/** The default material of each obstacle type (see `materialNameFor`). */
export const MATERIAL_DEFAULTS = {
  wall: 'wall',
  poly: 'wall',
  rect: 'wall',
  circle: 'tyre',
};

/**
 * Sprite blocks and modular seating require rectangles; path materials declare their allowed types.
 */
export function materialsForType(type) {
  return MATERIAL_NAMES.filter((name) => {
    const look = MATERIALS[name];
    return (!look.types || look.types.includes(type))
      && (type === 'rect' || (!look.variants && !look.rectOnly));
  });
}

/**
 * The width / height a shape of this material has to keep: its sprite is fitted to the whole rectangle,
 * so that ratio IS the rectangle's proportions. Used by the editor to keep the shape locked while it is
 * dragged (the painter fits the sprite whatever the editor sends — see paintBlockStrip).
 *
 * `name` is the material: every look with `variants` answers here (stands, houses, …), and they are all
 * offered for rectangles only (see materialsForType).
 */
export function variantRatio(name, variant = 0) {
  const chosen = materialVariant(name, variant);
  return chosen && chosen.depth > 0 ? chosen.length / chosen.depth : 1;
}

/**
 * The variant a material offers at an index (clamped to what it has), or null when that material isn't
 * one sprite fitted to a shape. `length`/`depth` are the size that sprite stands for in world units,
 * which is what both the painter and the editor scale it by.
 */
export function materialVariant(name, variant = 0) {
  const variants = MATERIALS[name]?.variants ?? [];
  return variants[variant] ?? variants[0] ?? null;
}

const known = (name) => (typeof name === 'string' && Object.hasOwn(MATERIALS, name) ? name : null);

/** The material an obstacle is made of: its own, else its type's, else the default (see the top). */
export function materialNameFor(track, obstacle) {
  return known(obstacle?.material)
    ?? known(track?.materials?.byType?.[obstacle?.type])
    ?? MATERIAL_DEFAULTS[obstacle?.type]
    ?? DEFAULT_MATERIAL;
}

/**
 * How a material looks on THIS track: the catalogue's definition with the track's own override on
 * top (`materials.use.<name>`), which is how one track swaps a look without changing every other.
 *
 * `extends: '<name>'` makes one material another's with a few fields changed — a texture variant. It
 * inherits EVERYTHING (colours, faces, contour, impact, …) and only what it names itself is different,
 * so `wall2: { extends: 'wall', image: … }` is a wall made of another picture and nothing else.
 */
export function materialLook(track, name) {
  const definition = MATERIALS[name] ?? MATERIALS[DEFAULT_MATERIAL];
  const inherited = definition.extends ? materialLook(track, definition.extends) : null;
  const merged = inherited ? { ...inherited, ...definition } : definition;
  const override = track?.materials?.use?.[name];
  return override ? { ...merged, ...override } : merged;
}

/**
 * The impact KIND of an obstacle's material — what the sounds and animations treat it as. A material
 * that only changes the look points at the behaviour it shares, either explicitly (`impact`) or by
 * extending the material it looks like (`extends`, e.g. wall2 → 'wall'): a texture variant is the same
 * thing to run into, so it needs no entry of its own in the sound catalogue. Without either, the kind is
 * the material's own name.
 */
export function materialImpact(track, obstacle) {
  const name = materialNameFor(track, obstacle);
  const look = materialLook(track, name);
  return look.impact ?? look.extends ?? name;
}
